/**
 * NotificationBell for the Business portal.
 *
 * Same UX as the shop NotificationBell but uses the business-session JWT
 * and links to /business/settings (notifications tab) instead of /account/notifications.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Bell, Package, ShieldAlert, Megaphone, Info, Check, Loader2,
  UserPlus, Truck, Settings2, ShoppingCart,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  getUnreadCount,
  getNotifications,
  markAsRead,
  markAllAsRead,
} from "../services/businessNotificationService";
import { useBusinessAuth } from "../context/BusinessAuthContext";
import { timeAgo } from "../../utils/timeAgo";
import { useTimeRefresh } from "../../hooks/useTimeRefresh";
import { resolveBusinessNotificationUrl } from "../../utils/notificationLinks";

const CAT_META = {
  ORDER:        { Icon: ShoppingCart, color: "text-secondary", bg: "bg-surface-sunken" },
  SECURITY:     { Icon: ShieldAlert,  color: "text-danger", bg: "bg-danger-subtle" },
  ACCOUNT:      { Icon: Settings2,    color: "text-secondary", bg: "bg-surface-sunken" },
  PROMO:        { Icon: Megaphone,    color: "text-secondary", bg: "bg-surface-sunken" },
  SYSTEM:       { Icon: Info,         color: "text-secondary", bg: "bg-surface-sunken" },
  INVITATION:   { Icon: UserPlus,     color: "text-secondary", bg: "bg-surface-sunken" },
  PROCUREMENT:  { Icon: Package,      color: "text-secondary", bg: "bg-surface-sunken" },
  DELIVERY:     { Icon: Truck,        color: "text-secondary", bg: "bg-surface-sunken" },
  SUPPLIER_OPS: { Icon: UserPlus,     color: "text-secondary", bg: "bg-surface-sunken" },
};

export default function BusinessNotificationBell() {
  const { isAuthenticated } = useBusinessAuth();
  const navigate = useNavigate();
  useTimeRefresh(30_000);

  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const mountedRef = useRef(true);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 8 });

  const updatePanelPosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPanelPos({ top: Math.max(8, rect.bottom + 8), right: Math.max(8, window.innerWidth - rect.right) });
  }, []);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // Close on outside click / ESC
  useEffect(() => {
    const onClick = (e) => {
      if (!anchorRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onEsc); };
  }, []);

  // Panel positioning
  useEffect(() => {
    if (!open) return;
    updatePanelPosition();
    const h = () => updatePanelPosition();
    window.addEventListener("resize", h);
    window.addEventListener("scroll", h, true);
    return () => { window.removeEventListener("resize", h); window.removeEventListener("scroll", h, true); };
  }, [open, updatePanelPosition]);

  // Initial unread count
  useEffect(() => {
    if (!isAuthenticated) { setCount(0); setItems([]); return; }
    getUnreadCount().then((c) => mountedRef.current && setCount(c)).catch(() => {});
  }, [isAuthenticated]);

  // Polling fallback (30s) + tab visibility
  useEffect(() => {
    if (!isAuthenticated) return;
    const refresh = () => { getUnreadCount().then((c) => mountedRef.current && setCount(c)).catch(() => {}); };
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    const interval = setInterval(refresh, 30_000);
    return () => { document.removeEventListener("visibilitychange", onVis); clearInterval(interval); };
  }, [isAuthenticated]);

  // Fetch when dropdown opens
  useEffect(() => {
    if (!open || !isAuthenticated) return;
    setLoading(true);
    getNotifications({ page: 1, limit: 6 })
      .then((res) => { if (mountedRef.current) setItems(res.items || []); })
      .catch(() => {})
      .finally(() => mountedRef.current && setLoading(false));
  }, [open, isAuthenticated]);

  const handleMarkRead = async (e, id) => {
    e.stopPropagation();
    try { await markAsRead(id); setItems((p) => p.map((n) => (n.id === id ? { ...n, isRead: true } : n))); setCount((p) => Math.max(0, p - 1)); } catch {}
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try { await markAllAsRead(); setItems((p) => p.map((n) => ({ ...n, isRead: true }))); setCount(0); } catch {}
    if (mountedRef.current) setMarkingAll(false);
  };

  const handleItemClick = (n) => {
    if (!n.isRead) { markAsRead(n.id).catch(() => {}); setItems((p) => p.map((x) => (x.id === n.id ? { ...x, isRead: true } : x))); setCount((p) => Math.max(0, p - 1)); }
    setOpen(false);
    const targetUrl = resolveBusinessNotificationUrl(n);
    if (targetUrl) navigate(targetUrl);
    else navigate("/business/notifications");
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative z-[120]" ref={anchorRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-control text-secondary transition-colors hover:bg-hover hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus max-md:h-11 max-md:w-11"
        aria-label={count > 0 ? `${count} unread notifications` : "Notifications"}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-counter tabular-nums text-on-accent ring-2 ring-surface">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ top: `${panelPos.top}px`, right: `${panelPos.right}px`, zIndex: 2000 }}
          className="fixed w-[calc(100vw-1rem)] max-w-sm overflow-hidden rounded-panel border border-default bg-surface-raised text-foreground shadow-menu sm:w-96"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-subtle px-4 py-3">
            <span className="text-panel-title text-foreground">Notifications</span>
            {count > 0 && (
              <button type="button" onClick={handleMarkAllRead} disabled={markingAll}
                className="rounded-control text-meta font-medium text-accent transition-colors hover:text-accent-hover disabled:opacity-40">
                {markingAll ? "Marking…" : "Mark all read"}
              </button>
            )}
          </div>

          {/* Items */}
          <div className="max-h-[min(360px,calc(100dvh-8rem))] overflow-y-auto scrollbar-fade">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-tertiary" /></div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={20} className="mx-auto mb-2 text-tertiary" />
                <p className="text-body-sm text-secondary">No notifications yet</p>
              </div>
            ) : (
              items.map((n) => {
                const cat = CAT_META[n.category] || CAT_META.SYSTEM;
                return (
                  <div key={n.id} role="button" tabIndex={0} onClick={() => handleItemClick(n)} onKeyDown={(e) => e.key === "Enter" && handleItemClick(n)}
                    className={["group flex gap-3 px-4 py-3 transition cursor-pointer border-b border-subtle last:border-b-0",
                      n.isRead ? "hover:bg-neutral-50 dark:hover:bg-neutral-800/50" : "bg-neutral-50/70 dark:bg-neutral-800/40 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    ].join(" ")}>
                    <div className={`shrink-0 flex items-center justify-center h-8 w-8 rounded-full ${cat.bg}`}>
                      <cat.Icon size={14} className={cat.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={["text-body-sm font-medium truncate", n.isRead ? "text-secondary" : "text-foreground"].join(" ")}>{n.title}</p>
                        <div className="shrink-0 flex items-center gap-1.5">
                          <span className="whitespace-nowrap text-meta text-tertiary">{timeAgo(n.createdAt)}</span>
                          {!n.isRead && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />}
                        </div>
                      </div>
                      <p className="mt-0.5 line-clamp-1 text-meta text-secondary">{n.body}</p>
                    </div>
                    {!n.isRead && (
                      <button type="button" onClick={(e) => handleMarkRead(e, n.id)} className="shrink-0 self-center opacity-100 p-1 rounded-md transition-opacity hover:bg-hover md:opacity-0 md:group-hover:opacity-100" title="Mark as read">
                        <Check size={12} className="text-secondary" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <Link to="/business/notifications" onClick={() => setOpen(false)}
            className="block w-full border-t border-subtle py-2.5 text-center text-body-sm font-medium text-accent transition-colors hover:bg-hover">
            See all notifications
          </Link>
        </div>,
        document.body
      )}
    </div>
  );
}
